import { SOCKET_URL } from '@/constants/api';
import _ from 'lodash';
export enum SOCKET_TYPES {
  COMMON = 'common',
  INSTRUMENT = 'instrument',
  ORDEBOOK = 'orderBook',
  PORTFOLIO = 'portfolio',
  KLINE = 'kline',
}
export enum SOCKET_METHODS {
  SUBSCRIBE = 'SUBSCRIBE',
  UNSUBSCRIBE = 'UNSUBSCRIBE',
}

export enum SOCKET_EVENTS {
  BLOCK_NUMBER_CHANGED = 'blockNumChanged',
  MARKET_LIST_CHANGED = 'marketListChanged',
  PORTFOLIO_CHANGED = 'portfolio',
  ORDER_BOOK_CHANGED = 'orderBook',
  INSTRUMENT_CHANGED = 'instrument',
  MARKET_PAIR_INFO_CHANGED = 'marketPairInfoChanged',
  KLINE_CHANGED = 'kline',
}

export interface SocketParamBase {
  type: SOCKET_TYPES;
  chainId: number;
}
export interface KlineSocketParam extends SocketParamBase {
  instrument: string;
  interval: string;
  expiry: number;
}
export interface OrderBookSocketParam extends SocketParamBase {
  instrument: string;
  expiry: number;
}
export interface InstrumentSocketParam extends SocketParamBase {
  instrument: string;
  expiry: number;
}
export interface PortfolioSocketParam extends SocketParamBase {
  userAddress: string;
}
export interface SocketPayload<T extends SocketParamBase> {
  id: number;
  method: SOCKET_METHODS;
  params: T;
}

export interface SocketData {
  stream: SOCKET_TYPES;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
}

export class SocketService {
  private static instance: SocketService;
  public socket: WebSocket | null = null;
  public url: string;
  private manualClose = false;
  private reconnectAttempts = 0;
  private maxReconnectDelay = 10_000; // ms
  private baseDelay = 500; // Initial reconnection delay
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private heartbeatTimeout: NodeJS.Timeout | null = null;
  private heartbeatInterval = 5_000; // ms
  private heartbeatResponseTimeout = 10_000; // ms
  private heartbeatPayload = 'ping';

  // Store pending events for re-emission
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private eventQueue: Map<string, any[]> = new Map();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private listeners: Map<string, ((...args: any[]) => void)[]> = new Map();

  /**
   * Active subscriptions that must be re-sent after reconnect.
   * Key is a stable string built from subscription params.
   */
  private activeSubscriptions: Map<string, SocketPayload<SocketParamBase>> = new Map();

  /**
   * Build a stable key for a subscription from its params.
   */
  private buildSubscriptionKey(params: SocketParamBase): string {
    try {
      return JSON.stringify(params);
    } catch {
      // Fallback to type + chainId if params are not serializable
      return `${params.type}:${params.chainId}`;
    }
  }

  public constructor() {
    this.url = SOCKET_URL;
  }

  public static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  public connect(): SocketService {
    this.manualClose = false;
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      console.record('socket', 'Existing connection or connecting, ignoring connect call');
      return this;
    }

    console.record('socket', 'Attempting to connect', this.url);
    this.socket = new WebSocket(this.url);

    this.setupSocketListeners();
    return this;
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      console.record('socket', 'Connection established');
      this.startHeartbeat();
      this.listeners.get('connect')?.forEach((cb) => cb());
      // On successful open, flush active subscriptions
      this.flushQueue();
    };

    this.socket.onmessage = (event) => {
      // Clear heartbeat timeout on any message received, assuming it's a pong or other valid response
      this.clearHeartbeatTimeout();
      let data: unknown = event.data;
      try {
        data = JSON.parse(event.data);
        if (_.isObject(data)) {
          const castedData = data as SocketData;
          console.record('socket', 'Received message', castedData);

          this.listeners.get(castedData.stream)?.forEach((cb) => cb(castedData.data));
        }
      } catch (e) {
        // Not JSON, return raw data
      }
    };

    this.socket.onerror = (event) => {
      console.error('WebSocket error:', event);
      this.listeners.get('error')?.forEach((cb) => cb(event));
    };

    this.socket.onclose = (event) => {
      this.stopHeartbeat();
      console.record(
        'socket',
        'Connection closed',
        event.code ? `code=${event.code}` : '',
        event.reason ? event.reason : '',
      );
      this.socket = null;
      this.listeners.get('disconnect')?.forEach((cb) => cb());

      if (!this.manualClose) {
        this.reconnectAttempts++;
        const delay = Math.min(this.maxReconnectDelay, this.baseDelay * Math.pow(1.5, this.reconnectAttempts));
        console.record(
          'socket',
          `Disconnected, attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`,
        );
        setTimeout(() => this.reconnect(), delay);
      } else {
        console.record('socket', 'Manually closed, will not reconnect');
      }
    };
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(this.heartbeatPayload);
        console.record('socket', 'Sending heartbeat');
        this.resetHeartbeatTimeout();
      }
    }, this.heartbeatInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.clearHeartbeatTimeout();
  }

  private resetHeartbeatTimeout(): void {
    this.clearHeartbeatTimeout();
    this.heartbeatTimeout = setTimeout(() => {
      console.warn('socket', 'Heartbeat response timed out, initiating reconnection.');
      this.reconnect();
    }, this.heartbeatResponseTimeout);
  }

  private clearHeartbeatTimeout(): void {
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  private reconnect(force = true): void {
    // If force is true, disconnect first without clearing the event queue
    if (force) {
      this.disconnect(false);
    }
    this.connect();
    this.flushQueue();
  }

  private flushQueue(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    // Re-send all active subscriptions on reconnect
    this.activeSubscriptions.forEach((payload, key) => {
      const payloadStr = typeof payload === 'string' ? (payload as unknown as string) : JSON.stringify(payload);
      try {
        this.socket?.send(payloadStr);
        console.record('socket', 'Resent subscription after reconnect', key);
      } catch (e) {
        console.error('socket', 'Failed to resend subscription', { key, e });
      }
    });
  }

  public disconnect(clearQueue = true): void {
    this.manualClose = true;
    this.stopHeartbeat();
    if (this.socket) {
      try {
        this.socket.close(1000, 'client close');
      } catch (e) {
        // ignore
      }
      this.socket = null;
    }
    // Clear event queue only if clearQueue is true
    if (clearQueue) {
      this.eventQueue.clear();
      this.activeSubscriptions.clear();
      // this.listeners.clear(); // Clear all listeners
    }
    console.record('socket', 'Connection close requested');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async emit(payload: SocketPayload<SocketParamBase>): Promise<void> {
    const payloadStr = typeof payload === 'string' ? payload : JSON.stringify(payload);

    // Track subscriptions and unsubscriptions
    if (payload.method === SOCKET_METHODS.SUBSCRIBE) {
      const key = this.buildSubscriptionKey(payload.params);
      this.activeSubscriptions.set(key, payload);
    } else if (payload.method === SOCKET_METHODS.UNSUBSCRIBE) {
      const key = this.buildSubscriptionKey(payload.params);
      this.activeSubscriptions.delete(key);
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(payloadStr);
    } else {
      while (this.socket?.readyState !== WebSocket.OPEN) {
        // Wait for the connection to open
        console.record('socket', '🚀 ~ SocketService ~ emit ~ waiting for connection to open');
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      this.socket.send(payloadStr);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public on(ev: string, cb: (...args: any[]) => void): void {
    if (!this.listeners.has(ev)) {
      this.listeners.set(ev, []);
    }
    this.listeners.get(ev)?.push(cb);
  }

  public off(ev: string): void {
    this.listeners.delete(ev);
  }

  public hasListeners(ev: SOCKET_TYPES): boolean {
    return this.listeners.has(ev);
  }
}

export default SocketService;
